// User Types
export type Gender = 'female' | 'male';
export type UserRole = 'user' | 'admin' | 'moderator' | 'analyst' | 'support';
export type UserStatus = 'active' | 'suspended' | 'banned';
export type AccountType = 'personal' | 'business';

export interface Profile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  profile_name: string;
  /** Chosen handle for /u/<username>; null until claimed (migration 110). */
  username?: string | null;
  username_changed_at?: string | null;
  dob: string;
  gender: Gender;
  avatar_url: string | null;
  bio: string | null;
  tags: string[];
  settings_radius: number;
  is_ghost_mode: boolean;
  is_women_only_mode: boolean;
  terms_accepted_at: string | null;
  role: UserRole;
  status: UserStatus;
  account_type: AccountType;
  notification_preferences: NotificationPreferences | null;
  last_lat: number | null;
  last_lng: number | null;
  welcomed_at: string | null;
  guide_dismissed_at: string | null;
  onboarding_step: string | null;
  business_onboarding_completed_at: string | null;
  allow_dms: boolean;
  created_at: string;
  updated_at: string;
}

// Business Types
export interface TimeRange {
  open: string;
  close: string;
}

// A day is closed (null / missing), a single range (legacy rows) or a list of
// ranges (split hours). Read through src/lib/businessHours.ts, never directly.
export type DayHours = TimeRange | TimeRange[] | null;

export interface BusinessOpeningHours {
  monday?: DayHours;
  tuesday?: DayHours;
  wednesday?: DayHours;
  thursday?: DayHours;
  friday?: DayHours;
  saturday?: DayHours;
  sunday?: DayHours;
}

export interface CreateVoucherForm {
  title: string;
  discount_text: string;
  original_price?: number;
  discounted_price?: number;
  category_id?: string;
  terms?: string;
  venue_name: string;
  venue_address: string;
  venue_lat: number;
  venue_lng: number;
  cover_image_url?: string;
  expires_at: string;
}

export type BusinessStatus =
  | 'pending_approval'
  | 'approved'
  | 'rejected'
  | 'suspended'
  | 'inactive'
  | 'archived';

export interface BusinessSocialLinks {
  website?: string;
  instagram?: string;
  facebook?: string;
  x?: string; // formerly Twitter
  tiktok?: string;
  linkedin?: string;
  youtube?: string;
  whatsapp?: string;
  phone?: string;
  email?: string;
}

export interface Business {
  id: string;
  owner_id: string | null;
  name: string;
  slug: string | null;
  logo_url: string | null;
  category: string;
  description: string | null;
  address: string | null;
  /** ISO code prices are shown in; see src/lib/currency.ts. */
  currency: string;
  opening_hours: BusinessOpeningHours | null;
  social_links: BusinessSocialLinks | null;
  status: BusinessStatus;
  verified: boolean;
  verified_at: string | null;
  rejection_reason: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  created_at: string;
  updated_at: string;
}

export type VerificationStatus = 'draft' | 'submitted' | 'in_review' | 'approved' | 'rejected';

export interface BusinessVerification {
  id: string;
  business_id: string;
  operator_selfie_path: string | null;
  id_document_path: string | null;
  selfie_with_id_path: string | null;
  registration_doc_path: string | null;
  additional_doc_paths: string[] | null;
  status: VerificationStatus;
  submitted_at: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  rejection_notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface BusinessWithOwner extends Business {
  owner: Profile;
}

export interface BusinessLocation {
  id: string;
  business_id: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
  is_primary: boolean;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
}

export const BUSINESS_CATEGORIES = [
  'Restaurant',
  'Cafe',
  'Bar & Pub',
  'Nightclub',
  'Retail Shop',
  'Gym & Fitness',
  'Salon & Spa',
  'Entertainment',
  'Hotel & Accommodation',
  'Takeaway',
  'Market & Pop-up',
  'Other',
] as const;

// Category Types
export interface Category {
  id: string;
  name: string;
  icon: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
}

// Event Types
export type JoinMode = 'request' | 'auto';
/**
 * How an event's time is shaped:
 *   single      one period on one day
 *   continuous  one multi-day period that never closes (24/7)
 *   sessions    per-day opening hours, days may be skipped
 *   recurring   a weekday pattern repeated weekly, one session per occurrence
 */
export type ScheduleKind = 'single' | 'continuous' | 'sessions' | 'recurring';
export type Audience = 'everyone' | 'women' | 'men';
export type EventStatus = 'draft' | 'active' | 'full' | 'expired' | 'cancelled' | 'deleted';

export interface Event {
  id: string;
  host_id: string;
  category_id: string;
  /** Optional child of category_id (migration 117). */
  subcategory_id?: string | null;
  title: string;
  description: string | null;
  venue_name: string;
  venue_address: string;
  venue_lat: number;
  venue_lng: number;
  venue_place_id: string | null;
  /** Start of the overall span = earliest session. Maintained by DB trigger. */
  start_time: string;
  /** End of the overall span = latest session. Null only before sessions exist. */
  end_time: string | null;
  schedule_kind: ScheduleKind;
  /**
   * Weekly event with no end date (migration 116). `top_up_rolling_events()`
   * keeps sessions in front of it, so it never expires while it is active.
   * Only ever true alongside `schedule_kind: 'recurring'`.
   */
  repeat_weekly?: boolean | null;
  /** Max approved guests per session (per day), excluding the host. `null` means unlimited. */
  capacity: number | null;
  /** Whether the host is one of the people going (migration 107). Absent or null counts as true. */
  host_attending?: boolean | null;
  join_mode: JoinMode;
  audience: Audience;
  custom_category: string | null;
  cover_image_url: string | null;
  /** URL handle, trigger-owned (migration 109). */
  slug?: string;
  /** Entry price. Null = not stated (row hidden), 0 = Free (migration 108). */
  price?: number | null;
  /** Currency for `price`; null falls back to the business currency, then GBP. */
  price_currency?: string | null;
  /** https link to book, buy tickets or register. */
  booking_url?: string | null;
  /** Short free text beside the price, max 80 chars (migration 108). */
  price_note?: string | null;
  /** Extra images (flyers, menus) shown as a strip under the description, max 6 (migration 108). */
  poster_urls?: string[] | null;
  allow_dms: boolean;
  business_id: string | null;
  status: EventStatus;
  created_at: string;
  expires_at: string;
}

/**
 * One period an event is actually on. A single-day event has one; a continuous
 * 24/7 event has one long one; a hybrid event has one per open day.
 * See `src/lib/eventSchedule.ts` for everything that reads these.
 */
export interface EventSession {
  id: string;
  event_id: string;
  starts_at: string;
  ends_at: string;
  created_at?: string | null;
}

export interface EventWithDetails extends Event {
  host: Profile;
  category: Category;
  /** Joined subcategory row, when the event has one. */
  subcategory?: { name: string; value: string } | null;
  participant_count: number;
  business?: Business | null;
  /** Empty or absent on queries that don't join them — use `sessionsOrFallback`. */
  event_sessions?: EventSession[];
}

// Participant Types
export type ParticipantStatus = 'pending' | 'approved' | 'rejected' | 'left';

export interface EventParticipant {
  id: string;
  event_id: string;
  user_id: string;
  status: ParticipantStatus;
  created_at: string;
  updated_at: string;
}

export interface EventParticipantWithProfile extends EventParticipant {
  user: Profile;
}

// Message Types
export interface Message {
  id: string;
  event_id: string;
  sender_id: string;
  content: string;
  created_at: string;
}

// Minimal business identity embedded alongside a message/DM sender so business
// accounts appear as the venue (logo + name) rather than a personal initial.
export interface SenderBusiness {
  id: string;
  name: string;
  logo_url: string | null;
}

export interface SenderProfile extends Profile {
  business?: SenderBusiness | null;
}

export interface MessageWithSender extends Message {
  sender: SenderProfile;
}

// Report Types
export type ReportStatus = 'pending' | 'reviewed' | 'dismissed' | 'actioned';

export interface Report {
  id: string;
  reporter_id: string;
  reported_user_id: string;
  event_id: string | null;
  reason: string;
  details: string | null;
  status: ReportStatus;
  admin_notes: string | null;
  created_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
}

// Block Types
export interface Block {
  id: string;
  blocker_id: string;
  blocked_id: string;
  created_at: string;
}

// Notification Types
export type NotificationType =
  | 'join_request'
  | 'request_approved'
  | 'request_declined'
  | 'new_message'
  | 'event_starting'
  | 'event_cancelled'
  | 'nearby_event'
  | 'voucher_shared'
  | 'voucher_redeemed'
  | 'participant_removed'
  | 'participant_left'
  | 'participant_rejoined'
  | 'review_prompt'
  | 'business_approved'
  | 'business_rejected'
  | 'admin_broadcast'
  | 'feedback'
  | 'new_dm'
  | 'event_shared'
  | 'new_follower'
  | 'event_updated';

// Notification preference keys (toggleable push types)
export interface NotificationPreferences {
  join_request: boolean;
  request_approved: boolean;
  request_declined: boolean;
  new_message: boolean;
  event_cancelled: boolean;
  event_starting: boolean;
  nearby_event: boolean;
  voucher_shared: boolean;
  event_shared: boolean;
  new_dm: boolean;
  new_follower: boolean;
  event_updated: boolean;
  participant_left: boolean;
  participant_rejoined: boolean;
  quiet_hours_enabled: boolean;
  quiet_hours_start: string; // "HH:MM" format
  quiet_hours_end: string;   // "HH:MM" format
  /** IANA zone the quiet-hours times are in (saved from the browser). Server falls back to Europe/London. */
  quiet_hours_tz?: string;
}

export interface Notification {
  id: string;
  user_id: string;
  type: NotificationType;
  title: string;
  body: string;
  data: Record<string, unknown>;
  is_read: boolean;
  created_at: string;
}

// Geolocation Types
export interface Coordinates {
  latitude: number;
  longitude: number;
}

// Form Types
export interface CreateEventForm {
  category_id: string;
  title: string;
  description?: string;
  venue_name: string;
  venue_address: string;
  venue_lat: number;
  venue_lng: number;
  venue_place_id?: string;
  start_time: string;
  /** Max approved guests, excluding the host. `null` means unlimited. */
  capacity: number | null;
  host_attending?: boolean;
  join_mode: JoinMode;
  audience: Audience;
}

export interface UpdateProfileForm {
  first_name: string;
  last_name?: string;
  profile_name?: string;
  bio?: string;
  tags: string[];
  avatar_url?: string;
}

export interface OnboardingForm {
  avatar_url: string;
  first_name: string;
  last_name: string;
  profile_name: string;
  dob: string;
  gender: Gender;
  tags: string[];
  bio?: string;
}

// Direct Message Types
export type DMMessageType = 'text' | 'voucher_share' | 'event_share';

export interface Conversation {
  id: string;
  participant_one: string;
  participant_two: string;
  last_message_at: string;
  created_at: string;
}

export interface ConversationWithDetails extends Conversation {
  other_user: SenderProfile;
  last_message: DirectMessage | null;
}

export interface DirectMessage {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string;
  message_type: DMMessageType;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface DirectMessageWithSender extends DirectMessage {
  sender: SenderProfile;
}

// Voucher Types
export type VoucherStatus = 'active' | 'expired' | 'redeemed' | 'deleted';

export interface Voucher {
  id: string;
  business_id: string;
  title: string;
  description: string | null;
  discount_text: string;
  original_price: number | null;
  discounted_price: number | null;
  redemption_code: string | null;
  redemption_limit: number | null;
  redemption_count: number;
  terms: string | null;
  cover_image_url: string | null;
  /** URL handle, trigger-owned (migration 109). */
  slug?: string;
  venue_name: string;
  venue_address: string;
  venue_lat: number;
  venue_lng: number;
  expires_at: string;
  status: VoucherStatus;
  created_at: string;
}

export interface VoucherWithDetails extends Voucher {
  business: Business;
}
